import React from "react";
import cx from "classnames";
import appConfig from "appConfig";
import { isAndroidWebView } from "@/utilities/display";
import RawGooglePayButton from "@google-pay/button-react";
import { useGooglePay } from "@/features/wallet/hooks/useGooglePay";
import { Pacman } from "@picklebet/ui";

import * as styles from "./GooglePayButton.module.scss";

type GooglePayButtonProps = {
  loading: boolean;
  isFinal: boolean;
  isValid: boolean;
  amount: number;
  onValidationError: () => void;
  size?: "small" | "large";
  onSuccess?: () => void;
  className?: string;
  processing?: boolean;
};

const GooglePayButton = ({
  loading,
  amount,
  isFinal,
  isValid,
  onValidationError,
  size = "large",
  onSuccess,
  className,
  processing = false,
}: GooglePayButtonProps) => {
  const { paymentRequest, deposit } = useGooglePay({
    amount,
    isFinal,
    loading,
    onSuccess,
  });
  return (
    <div
      className={cx(styles.container, className, {
        [styles.small]: size === "small",
        [styles.busy]: processing,
      })}
    >
      <RawGooglePayButton
        className={styles.gpay}
        environment={appConfig.ENV === "production" ? "PRODUCTION" : "TEST"}
        buttonColor="white"
        buttonSizeMode="fill"
        buttonType={"pay"}
        buttonRadius={30}
        paymentRequest={paymentRequest}
        onClick={(e) => {
          // Google Pay's sheet must be cancelled within this synchronous user
          // gesture, so validity is checked inline rather than via async submit
          if (!isValid) {
            e.preventDefault();
            onValidationError();
          }
        }}
        onError={(error) => {
          if (loading) return;
          console.error("Google Deposit Error:", error);
        }}
        onLoadPaymentData={async (response) => {
          const {
            paymentMethodData: {
              tokenizationData: { token },
              info: { cardDetails: panSuffix } = {},
            },
          } = response;

          deposit(token, panSuffix);
        }}
      />
      {isAndroidWebView() && !processing && (
        <div
          onClick={() => {
            // this overlay sits above RawGooglePayButton, so its onClick never
            // fires inside the Android app — validation has to happen here too
            if (!isValid) {
              onValidationError();
              return;
            }
            deposit();
          }}
          className={styles.clickCapture}
        ></div>
      )}

      {processing && (
        <div className={styles.processingOverlay}>
          <Pacman tone="light" size="default" />
        </div>
      )}
    </div>
  );
};

export { GooglePayButton };
